import React, { memo, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

// styles
import "./style.scss";

// antd component
import { Form, Button, Checkbox } from "antd";

// assets
import CustomIcons from "components/CustomIcons";

// components
import PreviewMedia from "components/Forms/Fields/PreviewMedia";
import CustomSelect from "components/Forms/Fields/CustomSelect";

// utils
// import { sortByKey } from 'utils/helpers/helper.general';
import {
  getAbsenceTypeLabel,
  getUndertakeLabel,
  getStatusesList,
} from "utils/helpers/helper.generateData";
import _local from "utils/localStorageUtils";
import { convertTimeToString, convertDateToString, convertDateToWeekDay } from "utils/moment/moment.utils";
import { useRefState } from "utils/hooks";



function ResumeForm({
  formData,
  isNew,
  isEdit,
  prevUnjustified,
  role,
  // actions
  nextStep,
  prevStep,
  goToStep,
  result,
  // default
  className,
}) {
  const childClassName = classNames("form-absence", className);
  const { t, i18n } = useTranslation();

  //const localUser = _local.getLocalUser();

  const isParentCanEdit = isEdit => {
    let access = false;
    if (!!isEdit?._id && !prevUnjustified && role == "parent") {
      if (
        (isEdit?.status == "sickness-finish" && isEdit?.media?.length > 0) ||
        isEdit?.status == "refused" ||
        isEdit?.type == "unjustified"
      ) {
        access = false;
      } else {
        access = true;
      }
    } else if (!isEdit?._id && !!isEdit && role == "parent" || !!isEdit?._id && !!prevUnjustified && role == "parent") {
      access = true;
    }
    return access;
  };

  const [isDisabled, setIsDisabled] = useState(false);
  const [parentCanEdit, setParentCanEdit] = useState(
    isParentCanEdit(isEdit || formData),
  );
  const [teacherCanEdit, setTeacherCanEdit] = useState(
    isEdit && role == "teacher",
  );

  const [absenceStatus, setAbsenceStatus] = useState(
    formData?.status || "pending",
  );
  const [isChildAbs, setIsChildAbs] = useState(
    !isNew && (!!isEdit?.origin || !!formData?.origin),
  );

  const [absenceData, absenceDataRef, setAbsenceData] = useRefState(
    (!!!isEdit?._id && formData) ||
      (!!isEdit?._id && !formData?._id && isEdit) ||
      (!!isEdit?._id && !!formData?._id && formData) ||
      formData,
  );

  const [AbsFormResume] = Form.useForm();

  /*
   * Check current form data from parent
   * Set old data to the form
   */
  useEffect(() => {
    if ((!!!isEdit?._id && formData) || !!isEdit?._id) {
      const mainData =
        (!!!isEdit?._id && formData) ||
        (!!isEdit?._id && !formData?._id && isEdit) ||
        (!!isEdit?._id && !!formData?._id && formData) ||
        formData;
      setAbsenceData(mainData);
      const absStatus = mainData?.status || "pending";
      AbsFormResume.setFieldsValue(mainData);
      AbsFormResume.setFieldsValue({
        status: absStatus,
      });
      setAbsenceStatus(absStatus);
      setParentCanEdit(prevStatus => {
        if (
          ( !!isEdit?._id && !prevUnjustified && role == "parent" ) &&
          (
            mainData?.status == "refused" ||
            mainData?.type == "unjustified" ||
            (isEdit?.status == "sickness-finish" && isEdit?.media?.length > 0)
          )
        ) {
          return false;
        } else if (((!isEdit || !isEdit?._id) && role == "parent") || !!isEdit?._id &&!!prevUnjustified && role == "parent") {
          return true;
        } else {
          return prevStatus;
        }
      });
      setTeacherCanEdit(prevStatus => {
        if (role != "teacher") {
          return false;
        } else if (!isEdit && role == "teacher") {
          return true;
        } else {
          return prevStatus;
        }
      });
      setIsDisabled(false);

      if (!isNew && !!mainData?.origin) {
        setIsChildAbs(!!mainData?.origin);
      }
    } else {
      setIsDisabled(false);
    }
  }, [formData, isEdit]);

  const onFinish = values => {
    result(values);
    !!nextStep && nextStep();
    AbsFormResume.resetFields();
  };

  const generateDuration = data => {
    if (data) {
      if (data?.duration == "more") {
        return (
          <>
            <div className="resume-fields__row">
              <div className="column helper">{t("common.from")}</div>
              <div className="column day">
                {convertDateToWeekDay(data?.date_from)}{" "}
              </div>
              <div className="column">
                {convertDateToString(data?.date_from)}
              </div>
              <div className="column time">
                {convertTimeToString(data?.time_from)}
              </div>
            </div>
            <div className="resume-fields__row">
              <div className="column helper">{t("common.to")}</div>
              <div className="column day">
                {convertDateToWeekDay(data?.date_to)}{" "}
              </div>
              <div className="column">{convertDateToString(data?.date_to)}</div>
              <div className="column time">
                {convertTimeToString(data?.time_to)}
              </div>
            </div>
          </>
        );
      } else {
        if (data?.type == "sickness" || data?.type == "unjustified") {
          return (
            <>
              <div className="resume-fields__row">
                <div className="column helper">{t("common.from")}</div>
                <div className="column day">
                  {" "}
                  {!!data?.date_from &&
                    convertDateToWeekDay(data?.date_from)}{" "}
                </div>
                <div className="column">
                  {!!data?.date_from && convertDateToString(data?.date_from)}
                </div>
                <div className="column time">
                  {!!data?.time_from && convertTimeToString(data?.time_from)}
                </div>
              </div>
              {data?.status != "sick" && (
                <div className="resume-fields__row">
                  <div className="column helper">{t("common.to")}</div>
                  <div className="column day">
                    {!!data?.date_to
                      ? convertDateToWeekDay(data?.date_to)
                      : convertDateToWeekDay(data?.date_from)}{" "}
                  </div>
                  <div className="column">
                    {!!data?.date_to
                      ? convertDateToString(data?.date_to)
                      : convertDateToString(data?.date_from)}
                  </div>
                  <div className="column time">
                    {!!data?.time_to
                      ? convertTimeToString(data?.time_to)
                      : convertTimeToString(data?.time_from)}
                  </div>
                </div>
              )}
            </>
          );
        } else {
          return (
            <>
              <div className="resume-fields__row">
                <div className="column helper">{t("common.from")}</div>
                <div className="column day">
                  {!!data?.date_from && convertDateToWeekDay(data?.date_from)}{" "}
                </div>
                <div className="column">
                  {!!data?.date_from && convertDateToString(data?.date_from)}
                </div>
                <div className="column time">
                  {!!data?.time_from && convertTimeToString(data?.time_from)}
                </div>
              </div>
              <div className="resume-fields__row">
                <div className="column helper">{t("common.to")}</div>
                <div className="column day">
                  {!!data?.recurring_date
                    ? convertDateToWeekDay(data?.recurring_date)
                    : !!data?.date_to
                    ? convertDateToWeekDay(data?.date_to)
                    : convertDateToWeekDay(data?.date_from)}{" "}
                </div>
                <div className="column">
                  {!!data?.recurring_date
                    ? convertDateToString(data?.recurring_date)
                    : !!data?.date_to
                    ? convertDateToString(data?.date_to)
                    : convertDateToString(data?.date_from)}
                </div>
                <div className="column time">
                  {!!data?.time_to
                    ? convertTimeToString(data?.time_to)
                    : convertTimeToString(data?.time_from)}
                </div>
              </div>
            </>
          );
        }
      }
    }
  };

  const goMediaStep = () => {
    const hasMedia =
      !absenceData?.media ||
      (absenceData?.media && absenceData?.media.length <= 0);
    const fomrMediaStep = isEdit && role == "parent" ? 1 : 4;
    const previewMediaStep = isEdit && role == "parent" ? 2 : 5;
    goToStep(hasMedia ? fomrMediaStep : previewMediaStep);
  };

  const goToPrev = () => {
    if (isEdit && role == "parent") {
      goMediaStep();
    } else {
      !!prevStep && prevStep();
    }
  };

  const getStatuses = useCallback(absenceType => getStatusesList(absenceType), [i18n?.language]);

  return (
    <div className={childClassName}>
      <Form
        form={AbsFormResume}
        className="form-absence__form"
        name="form_absence_resume"
        initialValues={{}}
        onFinish={onFinish}
        onValuesChange={values => {
          setIsDisabled(false);
          if (values?.status) {
            setAbsenceStatus(values?.status);
          }
        }}
      >
        {/* Motif de l’absence */}
        <div className="resume-section">
          <h4 className="resume-title">{t("absences.absencesReason")}</h4>
          {!isChildAbs &&
            (
              (
                isNew && parentCanEdit && !(absenceData?.type == "medical" && role == "parent") ||
                !!prevUnjustified && parentCanEdit && !(absenceData?.type == "medical" && role == "parent")
              ) ||
              (teacherCanEdit &&
                !(absenceData?.type == "medical" && role == "teacher"))
            ) && (
              <CustomIcons
                type="edit"
                className="resume-edit"
                onClick={() => goToStep(2)}
              />
            )}
          <div className="resume-fields">
            {`${getAbsenceTypeLabel(absenceData?.type)}${
              !!absenceData?.reason ? ` (${absenceData?.reason})` : ""
            }`}
          </div>
        </div>

        {/* Durée de l'absence */}
        <div className="resume-section">
          <h4 className="resume-title">{t("absences.absenceDuration")}</h4>
          {((isNew && parentCanEdit || !!prevUnjustified && parentCanEdit) || teacherCanEdit) && (
            <CustomIcons
              type="edit"
              className="resume-edit"
              onClick={() => goToStep(3)}
            />
          )}
          <div className="resume-fields">{generateDuration(absenceData)}</div>
        </div>

        {/* Justificatif */}
        <div className="resume-section">
          <h4 className="resume-title">{t("absences.proof")}</h4>
          {((isNew && parentCanEdit || parentCanEdit && !(absenceData?.status == "refused" || absenceData?.status == "accepted")) || teacherCanEdit) && (
            <CustomIcons
              type="edit"
              className="resume-edit"
              onClick={goMediaStep}
            />
          )}
          <div className="resume-fields">
            {(!absenceData?.media || absenceData?.media?.length == 0) &&
              t("absences.emptyProof")}
            {absenceData?.media?.length > 0 && (
              <PreviewMedia file={absenceData?.media?.[0]} showTitle={false} />
            )}
          </div>
        </div>

        {/* Déplacement */}
        {!!absenceData?.parents_undertake && (
          <>
            <div className="resume-section">
              <h4 className="resume-title">{t("absences.displacement")}</h4>
              {((isNew && parentCanEdit || !!prevUnjustified && parentCanEdit ) || teacherCanEdit) && (
                <CustomIcons
                  type="edit"
                  className="resume-edit"
                  onClick={() => goToStep(6)}
                />
              )}
              <div className="resume-fields">
                {getUndertakeLabel(absenceData?.parents_undertake)}
              </div>
            </div>
          </>
        )}

        {/* Remarque */}
        {(
          <div className="resume-section">
            <h4 className="resume-title">{t("common.note")}</h4>
            {((isNew && parentCanEdit || parentCanEdit && !(absenceData?.status == "refused" || absenceData?.status == "accepted")) || teacherCanEdit) && (
              <CustomIcons
                type="edit"
                className="resume-edit"
                onClick={() => !isNew && parentCanEdit ? goToStep(3) : goToStep(7)}
              />
            )}
            <div
              className="resume-fields"
              dangerouslySetInnerHTML={{
                __html: absenceData?.remark?.replace(/\n/g, '<br>')
              }} 
            />
          </div>
        )}

        {/* Status */}
        {role == "teacher" && (
          <div className="resume-section">
            <div className="resume-fields">
              <CustomSelect
                className={`accent-label select-status ${absenceStatus}`}
                label={t("common.status")}
                value={absenceStatus}
                placeholder={t("inputs.select")}
                name={`status`}
                options={
                  (!!absenceData?.type && getStatuses(absenceData?.type)) ||
                  []
                }
              />
            </div>
          </div>
        )}

        {/* Absence injustifiée */}
        {(
          <div className="resume-section">
            <div className="resume-fields">
              <Form.Item
                name="unjustified"
                key="unjustified"
                className="forms-field forms-field--checkbox"
                valuePropName="checked"
              >
                <Checkbox
                  disabled={role == "parent" || absenceData?.type == "unjustified"}
                  checked={
                    absenceData?.type == "unjustified" || !!absenceData?.unjustified
                  }
                >
                  <span className="checkbox-label">{t("absences.unjustifiedAbsence")}</span>
                </Checkbox>
              </Form.Item>
            </div>
          </div>
        )}

        {(parentCanEdit || teacherCanEdit) && (
          <div className="absence-actions-wrapper">
            <Button className="btn-previous" onClick={goToPrev}>
              {t("common.previous")}
            </Button>
            <Button
              className="confirm-btn"
              form="form_absence_resume"
              htmlType="submit"
              disabled={isDisabled}
            >
              {t("common.confirm")}
            </Button>
          </div>
        )}
      </Form>
    </div>
  );
}

ResumeForm.defaultProps = {
  isNew: false,
  isEdit: false,
};
ResumeForm.propTypes = {
  formData: PropTypes.oneOfType([
    PropTypes.bool.isRequired,
    PropTypes.object.isRequired,
    PropTypes.string.isRequired,
  ]),
  role: PropTypes.string.isRequired,
  result: PropTypes.func.isRequired,
  nextStep: PropTypes.func.isRequired,
  prevStep: PropTypes.func.isRequired,
  goToStep: PropTypes.func.isRequired,
};

export default memo(ResumeForm);
